(() => {
  const ROOT_SELECTOR = '[data-brand-page-endless]';

  class BrandPageEndless {
    constructor(root) {
      this.root = root;
      this.rowsContainer = root.querySelector('[data-brand-page-rows]');
      this.sentinel = root.querySelector('[data-brand-page-sentinel]');
      this.loadMoreButton = root.querySelector('[data-brand-page-load-more]');
      this.statusMessage = root.querySelector('[data-brand-page-status]');
      this.rowSectionId = root.dataset.rowSectionId || 'brand-page-row-proxy';
      this.chunkSize = Number(root.dataset.chunkSize || 4);
      this.loadingLabel = root.dataset.loadingLabel || 'Loading brands...';
      this.errorLabel = root.dataset.errorLabel || 'Could not load more brands.';
      this.loadedLabel = root.dataset.loadedLabel || 'All brands loaded.';
      this.preloadMargin = root.dataset.preloadMargin || '700px';
      this.handles = this.parseHandles(root.dataset.brandHandles);
      this.cursor = 0;
      this.loading = false;

      this.boundOnClick = this.onLoadMoreClick.bind(this);
      this.boundOnIntersect = this.onIntersect.bind(this);
    }

    parseHandles(rawValue) {
      if (!rawValue) return [];
      try {
        const parsed = JSON.parse(rawValue);
        return Array.isArray(parsed) ? parsed.filter((item) => typeof item === 'string' && item.length > 0) : [];
      } catch {
        return [];
      }
    }

    init() {
      if (!this.rowsContainer) return;

      if (this.loadMoreButton) {
        this.loadMoreButton.addEventListener('click', this.boundOnClick);
      }

      if (this.handles.length === 0) {
        this.finish();
        return;
      }

      if ('IntersectionObserver' in window && this.sentinel) {
        this.observer = new IntersectionObserver(this.boundOnIntersect, {
          rootMargin: `0px 0px ${this.preloadMargin} 0px`,
          threshold: 0
        });
        this.observer.observe(this.sentinel);
      }
    }

    destroy() {
      if (this.loadMoreButton) {
        this.loadMoreButton.removeEventListener('click', this.boundOnClick);
      }
      if (this.observer) {
        this.observer.disconnect();
      }
    }

    setStatus(message) {
      if (!this.statusMessage) return;
      this.statusMessage.textContent = message;
    }

    setLoadingState(isLoading) {
      this.loading = isLoading;
      if (!this.loadMoreButton) return;
      this.loadMoreButton.disabled = isLoading;
      this.loadMoreButton.classList.toggle('is-loading', isLoading);
    }

    finish() {
      this.setStatus(this.loadedLabel);
      if (this.loadMoreButton) {
        this.loadMoreButton.hidden = true;
      }
      if (this.sentinel) {
        this.sentinel.hidden = true;
      }
      if (this.observer) {
        this.observer.disconnect();
      }
    }

    onLoadMoreClick(event) {
      event.preventDefault();
      this.loadNextChunk();
    }

    onIntersect(entries) {
      const isVisible = entries.some((entry) => entry.isIntersecting);
      if (isVisible) {
        this.loadNextChunk();
      }
    }

    async fetchRow(handle) {
      const collectionPath = `${window.Shopify.routes.root}collections/${encodeURIComponent(handle)}`;
      const url = new URL(collectionPath, window.location.origin);
      url.searchParams.set('section_id', this.rowSectionId);

      const response = await fetch(url.toString(), { method: 'GET' });
      if (!response.ok) {
        throw new Error(`Brand row fetch failed: ${response.status}`);
      }

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      return doc.querySelector('[data-brand-row]');
    }

    async loadNextChunk() {
      if (this.loading) return;

      const chunk = this.handles.slice(this.cursor, this.cursor + this.chunkSize);
      if (chunk.length === 0) {
        this.finish();
        return;
      }

      this.setLoadingState(true);
      this.setStatus(this.loadingLabel);

      try {
        const rows = await Promise.all(chunk.map((handle) => this.fetchRow(handle)));
        rows.forEach((row) => {
          if (row) {
            this.rowsContainer.appendChild(row);
          }
        });

        this.cursor += chunk.length;
        this.setStatus('');

        if (this.cursor >= this.handles.length) {
          this.finish();
        }
      } catch (error) {
        console.warn(error);
        this.setStatus(this.errorLabel);
      } finally {
        this.setLoadingState(false);
      }
    }
  }

  const init = (scope = document) => {
    const roots = scope.matches && scope.matches(ROOT_SELECTOR)
      ? [scope]
      : Array.from(scope.querySelectorAll(ROOT_SELECTOR));

    roots.forEach((root) => {
      if (root.__brandPageEndless) return;
      root.__brandPageEndless = new BrandPageEndless(root);
      root.__brandPageEndless.init();
    });
  };

  const destroy = (scope = document) => {
    const roots = scope.matches && scope.matches(ROOT_SELECTOR)
      ? [scope]
      : Array.from(scope.querySelectorAll(ROOT_SELECTOR));

    roots.forEach((root) => {
      if (!root.__brandPageEndless) return;
      root.__brandPageEndless.destroy();
      delete root.__brandPageEndless;
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroy(event.target));
})();
